import { useEffect, useState } from "react";
import axios from "axios";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  { name: "Aisha K.", relation: "Mum of Zaid", message: "My son couldn't wait for playgroup mornings. He came home singing, drawing and reciting little duas. Cheeky Monkeys made him confident and kind." },
  { name: "Fatima M.", relation: "Mum of two graduates", message: "20 years and still full of warmth. Both my kids started here — the teachers know every child like family." },
  { name: "Yusuf & Layla H.", relation: "Parents", message: "Balanced, loving and grounded in beautiful Islamic values. We felt at peace sending our daughter each day." },
  { name: "Nadia S.", relation: "Mum of Zara", message: "The activities, songs and Arabic basics gave Zara such a strong start. Ridgeway is lucky to have Cheeky Monkeys." },
  { name: "Imran & Sarah A.", relation: "Ridgeway parents", message: "A safe, joyful little home. Our son learned to share, to say Bismillah and to make lifelong friends." },
];

export default function Testimonials() {
  const [items, setItems] = useState(FALLBACK);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/testimonials`).then((res) => {
      if (alive && Array.isArray(res.data) && res.data.length > 0) setItems(res.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);
  const active = items[idx];

  return (
    <section id="testimonials" className="relative py-20 md:py-28" data-testid="testimonials-section">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <p className="uppercase tracking-widest text-sm font-bold text-brand-purple">Parents say</p>
        <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-brand-ink">
          Loved by our <span className="text-brand-red">Ridgeway</span> families.
        </h2>

        <div className="mt-12 relative rounded-3xl border-[3px] border-brand-ink bg-white p-8 md:p-12 shadow-brutal">
          <Quote size={48} className="text-brand-yellow absolute -top-6 left-6 rotate-[-10deg]" fill="#FFD166" strokeWidth={2.5} />
          <div className="flex justify-center gap-1 mb-4">
            {[0,1,2,3,4].map((s) => (
              <Star key={s} size={22} fill="#FFD166" className="text-brand-yellowDark" strokeWidth={2} />
            ))}
          </div>
          <p className="font-display text-2xl md:text-3xl text-brand-ink leading-snug" data-testid="testimonial-message">
            “{active?.message}”
          </p>
          <div className="mt-6">
            <p className="font-bold text-brand-red text-lg">{active?.name}</p>
            <p className="text-brand-ink/70 text-sm">{active?.relation}</p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              onClick={prev}
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
              className="w-11 h-11 rounded-full border-[3px] border-brand-ink bg-white hover:bg-brand-yellow transition-colors flex items-center justify-center"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to testimonial ${i+1}`}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all ${i === idx ? "w-6 bg-brand-red" : "w-2.5 bg-brand-ink/30"}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              aria-label="Next testimonial"
              data-testid="testimonial-next"
              className="w-11 h-11 rounded-full border-[3px] border-brand-ink bg-white hover:bg-brand-yellow transition-colors flex items-center justify-center"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
